import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { QrCode, Copy, ExternalLink, RefreshCw, Clock, CheckCircle2, XCircle, FileText, User, Loader2, Building2, Download, Globe, CalendarDays, Printer } from 'lucide-react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import axios from 'axios';
import PortalLayout from './PortalLayout';
import { getGlobalPropertyId, setGlobalProperty } from './propertySync';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STATUS_MAP = {
  pending: { label: 'Awaiting Guest', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400', icon: Clock },
  active: { label: 'Active', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', icon: CheckCircle2 },
  submitted: { label: 'Submitted', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', icon: FileText },
  validated: { label: 'Validated', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', icon: XCircle },
  failed: { label: 'Failed', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', icon: XCircle },
};

export default function PortalQRInvoice() {
  const [tab, setTab] = useState('generate');
  const [properties, setProperties] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [propertyFilter, setPropertyFilter] = useState(() => getGlobalPropertyId() || 'all');
  const [qrType, setQrType] = useState('general');

  // General QR state — persistent per property
  const [generalQR, setGeneralQR] = useState(null);
  const [loadingGeneralQR, setLoadingGeneralQR] = useState(false);

  // Booking QR form
  const [form, setForm] = useState({ property_id: '', description: 'Accommodation charges', amount: '', booking_reference: '' });

  // Inline QR result (for booking QR after generation)
  const [inlineQR, setInlineQR] = useState(null);

  const qrCanvasRef = useRef(null);
  const token = localStorage.getItem('einvoice_token');
  const headers = { Authorization: `Bearer ${token}` };
  const baseUrl = window.location.origin;

  useEffect(() => {
    if (!token) return;
    loadProperties();
    loadRequests();
    const h = (e) => { const p = e.detail; setPropertyFilter(p ? p.id : 'all'); };
    window.addEventListener('property-changed', h);
    return () => window.removeEventListener('property-changed', h);
  }, []);

  useEffect(() => { loadRequests(); }, [propertyFilter]);

  // Auto-fetch general QR when property changes
  useEffect(() => {
    if (form.property_id) fetchGeneralQR(form.property_id);
  }, [form.property_id]);

  const loadProperties = async () => {
    try {
      const { data } = await axios.get(`${API}/api/einvoice/portal/properties`, { headers });
      setProperties(data.properties || []);
      if ((data.properties || []).length > 0 && !form.property_id) {
        setForm(f => ({ ...f, property_id: data.properties[0].id }));
      }
    } catch {}
  };

  const fetchGeneralQR = async (propertyId) => {
    setLoadingGeneralQR(true);
    try {
      const { data } = await axios.get(`${API}/api/einvoice/qr/general/${propertyId}`, { headers });
      if (data.exists) {
        setGeneralQR(data);
      } else {
        setGeneralQR(null);
      }
    } catch {
      setGeneralQR(null);
    } finally { setLoadingGeneralQR(false); }
  };

  const loadRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (propertyFilter && propertyFilter !== 'all') params.append('property_id', propertyFilter);
      const { data } = await axios.get(`${API}/api/einvoice/qr/requests?${params}`, { headers });
      setRequests(data.requests || []);
    } catch {} finally { setLoading(false); }
  };

  const handleGenerateGeneral = async () => {
    if (!form.property_id) { toast.error('Select a property'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/einvoice/qr/generate`, {
        property_id: form.property_id,
        qr_type: 'general',
        description: form.description || 'Accommodation charges',
      }, { headers });
      setGeneralQR({ exists: true, token: data.token, form_url: data.form_url, property_name: data.property_name, submission_count: data.submission_count || 0 });
      toast.success(data.reused ? 'Existing General QR loaded' : 'General QR generated!');
      loadRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    } finally { setGenerating(false); }
  };

  const handleGenerateBooking = async () => {
    if (!form.property_id) { toast.error('Select a property'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Amount is required for booking QR'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/einvoice/qr/generate`, {
        property_id: form.property_id,
        qr_type: 'booking',
        description: form.description || 'Accommodation charges',
        amount: parseFloat(form.amount),
        booking_reference: form.booking_reference || null,
      }, { headers });
      setInlineQR(data);
      toast.success('Booking QR generated!');
      loadRequests();
      setForm(f => ({ ...f, description: 'Accommodation charges', amount: '', booking_reference: '' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    } finally { setGenerating(false); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const downloadQRPng = (qrToken, label) => {
    const canvas = document.querySelector(`[data-qr-canvas="${qrToken}"] canvas`);
    if (!canvas) { toast.error('QR not ready yet'); return; }
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr-einvoice-${label || qrToken}.png`;
    a.click();
  };

  const printQR = (qrToken) => {
    const canvas = document.querySelector(`[data-qr-canvas="${qrToken}"] canvas`);
    if (!canvas) { toast.error('QR not ready yet'); return; }
    const url = canvas.toDataURL('image/png');
    const win = window.open('', '_blank');
    const propName = properties.find(p => p.id === form.property_id)?.property_name || '';
    win.document.write(`
      <html><head><title>QR Code - ${propName}</title>
      <style>body{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0;font-family:system-ui,sans-serif}
      img{width:300px;height:300px}h2{margin:0 0 8px;font-size:18px}p{margin:4px 0;font-size:12px;color:#666}
      @media print{body{padding:20mm}}</style></head>
      <body><h2>StayMii e-Invoice</h2><p>${propName}</p>
      <img src="${url}" /><p style="margin-top:12px;font-size:11px">Scan to submit your e-Invoice details</p>
      <script>window.onload=()=>window.print()</script></body></html>
    `);
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return '-'; }
  };

  return (
    <PortalLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="qr-invoice-page">
        <div>
          <h1 className="text-2xl font-bold text-[#4169E1]" data-testid="qr-title">QR e-Invoice</h1>
          <p className="text-sm text-slate-500 dark:text-gray-400">Generate QR codes for guests to self-submit e-Invoice details</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100 dark:bg-zinc-800 p-1 rounded-lg" data-testid="qr-tabs">
            <TabsTrigger value="generate" className="flex items-center gap-1.5 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-[#4169E1] px-4" data-testid="tab-generate">
              <QrCode className="w-3.5 h-3.5" /> Generate QR
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-1.5 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-[#4169E1] px-4" data-testid="tab-requests">
              <FileText className="w-3.5 h-3.5" /> Requests
              {requests.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-1 bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{requests.filter(r => r.status === 'pending').length}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ─── Generate Tab ─── */}
          <TabsContent value="generate" className="mt-6 space-y-6">
            {/* Property Selector */}
            <div className="max-w-md">
              <Label className="text-sm mb-1.5 block">Property</Label>
              <Select value={form.property_id} onValueChange={v => setForm(f => ({ ...f, property_id: v }))}>
                <SelectTrigger data-testid="qr-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* ─── YOUR GENERAL QR CODE ─── */}
            <Card className="border-2 border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50/50 to-white dark:from-purple-900/10 dark:to-zinc-900" data-testid="general-qr-section">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Globe className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <CardTitle className="text-base dark:text-white">Your General QR Code</CardTitle>
                    <CardDescription className="text-xs">Reusable — print once, use for every guest</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingGeneralQR ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>
                ) : generalQR ? (
                  <div className="flex flex-col md:flex-row items-center gap-6" data-testid="general-qr-display">
                    {/* QR Code */}
                    <div className="shrink-0">
                      <div className="bg-white p-4 rounded-xl shadow-lg border border-purple-100 inline-block" data-qr-canvas={generalQR.token}>
                        <QRCodeCanvas value={`${baseUrl}/einvoice/form/${generalQR.token}`} size={200} level="H" includeMargin />
                      </div>
                    </div>
                    {/* Info + Actions */}
                    <div className="flex-1 text-center md:text-left space-y-3">
                      <div>
                        <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px] mb-1">
                          <Globe className="w-3 h-3 mr-1" /> Reusable General QR
                        </Badge>
                        <p className="text-sm font-semibold text-slate-800 dark:text-white">{generalQR.property_name}</p>
                        <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                          {generalQR.submission_count || 0} submission{(generalQR.submission_count || 0) !== 1 ? 's' : ''} received
                        </p>
                      </div>
                      {/* Link */}
                      <div className="flex items-center gap-2 bg-slate-50 dark:bg-zinc-800 rounded-lg p-2">
                        <input readOnly value={`${baseUrl}/einvoice/form/${generalQR.token}`} className="flex-1 bg-transparent text-xs text-slate-600 dark:text-gray-300 outline-none min-w-0" data-testid="general-qr-url" />
                        <Button size="sm" variant="ghost" onClick={() => copyLink(`${baseUrl}/einvoice/form/${generalQR.token}`)} className="h-7 px-2 shrink-0" data-testid="copy-general-link">
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => downloadQRPng(generalQR.token, 'general')} className="text-xs" data-testid="download-general-png">
                          <Download className="w-3.5 h-3.5 mr-1.5" /> Download PNG
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => printQR(generalQR.token)} className="text-xs" data-testid="print-general-qr">
                          <Printer className="w-3.5 h-3.5 mr-1.5" /> Print QR
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(`${baseUrl}/einvoice/form/${generalQR.token}`, '_blank')} className="text-xs" data-testid="open-general-form">
                          <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open Form
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6" data-testid="no-general-qr">
                    <QrCode className="w-12 h-12 mx-auto text-purple-300 dark:text-purple-700 mb-3" />
                    <p className="text-sm text-slate-600 dark:text-gray-400 mb-1">No General QR code yet for this property</p>
                    <p className="text-xs text-slate-400 dark:text-gray-500 mb-4">Generate one and print it at your counter — guests scan and self-handle everything</p>
                    <Button onClick={handleGenerateGeneral} disabled={generating || !form.property_id} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="generate-general-qr-btn">
                      {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
                      Generate General QR
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ─── BOOKING QR GENERATOR ─── */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card data-testid="booking-qr-form-card">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <CalendarDays className="w-4 h-4 text-[#4169E1]" />
                    </div>
                    <div>
                      <CardTitle className="text-base dark:text-white">Per Booking QR</CardTitle>
                      <CardDescription className="text-xs">One-time QR tied to a specific booking</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm">Booking Reference</Label>
                    <Input value={form.booking_reference} onChange={e => setForm(f => ({ ...f, booking_reference: e.target.value }))} placeholder="e.g. BK-2026-001" data-testid="qr-booking-ref" />
                  </div>
                  <div>
                    <Label className="text-sm">Amount (RM) *</Label>
                    <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="350.00" data-testid="qr-amount" />
                  </div>
                  <div>
                    <Label className="text-sm">Description</Label>
                    <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Room charges" data-testid="qr-description" />
                  </div>
                  <Button onClick={handleGenerateBooking} disabled={generating || !form.property_id} className="w-full bg-[#4169E1] hover:bg-[#3558c8] text-white" data-testid="generate-booking-qr-btn">
                    {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
                    Generate Booking QR
                  </Button>
                </CardContent>
              </Card>

              {/* Inline Booking QR Result */}
              {inlineQR ? (
                <Card className="border-2 border-blue-200 dark:border-blue-800" data-testid="inline-booking-qr">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-center dark:text-white">Booking QR Code</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center space-y-3">
                    <div className="bg-white p-4 rounded-xl shadow-md inline-block" data-qr-canvas={inlineQR.token}>
                      <QRCodeCanvas value={`${baseUrl}${inlineQR.form_url}`} size={180} level="H" includeMargin />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-gray-200">{inlineQR.property_name}</p>
                      {inlineQR.amount && <p className="text-lg font-bold text-[#4169E1]">RM {inlineQR.amount.toFixed(2)}</p>}
                    </div>
                    <div className="flex items-center gap-2 bg-slate-50 dark:bg-zinc-800 rounded-lg p-2">
                      <input readOnly value={`${baseUrl}${inlineQR.form_url}`} className="flex-1 bg-transparent text-xs text-slate-600 dark:text-gray-300 outline-none min-w-0" />
                      <Button size="sm" variant="ghost" onClick={() => copyLink(`${baseUrl}${inlineQR.form_url}`)} className="h-7 px-2 shrink-0" data-testid="copy-booking-link">
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => downloadQRPng(inlineQR.token, 'booking')} data-testid="download-booking-png">
                        <Download className="w-3.5 h-3.5 mr-1" /> PNG
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => printQR(inlineQR.token)} data-testid="print-booking-qr">
                        <Printer className="w-3.5 h-3.5 mr-1" /> Print
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => window.open(`${baseUrl}${inlineQR.form_url}`, '_blank')} data-testid="open-booking-form">
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base dark:text-white">How It Works</CardTitle>
                    <CardDescription>Per-booking QR flow</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { step: '1', title: 'Generate QR', desc: 'Create a QR code for a specific booking and amount' },
                        { step: '2', title: 'Share with Guest', desc: 'Print or send the QR to your guest' },
                        { step: '3', title: 'Guest Fills Details', desc: 'Guest scans and provides billing information' },
                        { step: '4', title: 'Auto e-Invoice', desc: 'System submits to LHDN and emails the guest' },
                      ].map(s => (
                        <div key={s.step} className="flex gap-3">
                          <div className="w-7 h-7 rounded-full bg-[#4169E1]/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-[#4169E1]">{s.step}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-white">{s.title}</p>
                            <p className="text-xs text-slate-500 dark:text-gray-400">{s.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* ─── Requests Tab ─── */}
          <TabsContent value="requests" className="mt-6">
            <div className="space-y-4" data-testid="qr-requests-panel">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500 dark:text-gray-400">{requests.length} request(s)</p>
                <div className="flex items-center gap-2">
                  <Select value={propertyFilter} onValueChange={v => { setPropertyFilter(v); setGlobalProperty(v === 'all' ? null : v, properties); }}>
                    <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="All Properties" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Properties</SelectItem>
                      {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={loadRequests} className="h-8" data-testid="refresh-requests">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#4169E1]" /></div>
              ) : requests.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-sm text-slate-400 dark:text-gray-500">
                  <QrCode className="w-10 h-10 mx-auto text-slate-300 dark:text-gray-600 mb-3" />
                  No QR requests yet.
                </CardContent></Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="qr-requests-table">
                        <thead>
                          <tr className="border-b border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800/50">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Type</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Property</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Ref</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Amount</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Guest</th>
                            <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Status</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Created</th>
                            <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-zinc-800">
                          {requests.map(r => {
                            const isGeneral = r.qr_type === 'general';
                            const st = STATUS_MAP[r.status] || STATUS_MAP.pending;
                            const StIcon = st.icon;
                            return (
                              <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-zinc-800/50" data-testid={`qr-row-${r.id}`}>
                                <td className="py-3 px-4">
                                  <Badge variant="outline" className={`text-[10px] ${isGeneral ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800' : 'bg-slate-50 dark:bg-zinc-800 text-slate-600 dark:text-gray-400 border-slate-200 dark:border-zinc-700'}`}>
                                    {isGeneral ? <Globe className="w-3 h-3 mr-1" /> : <CalendarDays className="w-3 h-3 mr-1" />}
                                    {isGeneral ? 'General' : 'Booking'}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4 text-sm text-slate-700 dark:text-gray-200">{r.property_name || '-'}</td>
                                <td className="py-3 px-4 text-xs text-slate-500 dark:text-gray-400 font-mono">
                                  {isGeneral ? (
                                    <span className="text-purple-500">{r.submission_count || 0} submissions</span>
                                  ) : (r.booking_reference || '-')}
                                </td>
                                <td className="py-3 px-4 text-sm text-right font-medium text-slate-900 dark:text-white">{r.amount ? `RM ${r.amount.toFixed(2)}` : <span className="text-slate-400 text-xs">From system</span>}</td>
                                <td className="py-3 px-4">
                                  {r.guest_data ? (
                                    <div className="flex items-center gap-1.5">
                                      <User className="w-3 h-3 text-slate-400" />
                                      <span className="text-sm text-slate-700 dark:text-gray-200">{r.guest_data.name}</span>
                                    </div>
                                  ) : <span className="text-xs text-slate-400">-</span>}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <Badge variant="outline" className={`text-[10px] ${st.color}`}>
                                    <StIcon className="w-3 h-3 mr-1" /> {st.label}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4 text-xs text-slate-500 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                                <td className="py-3 px-4">
                                  <div className="flex justify-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => copyLink(`${baseUrl}/einvoice/form/${r.token}`)} title="Copy link">
                                      <Copy className="w-3.5 h-3.5 text-slate-500" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PortalLayout>
  );
}
